/*
 * Copyright (C) 2020 Graylog, Inc.
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the Server Side Public License, version 1,
 * as published by MongoDB, Inc.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * Server Side Public License for more details.
 *
 * You should have received a copy of the Server Side Public License
 * along with this program. If not, see
 * <http://www.mongodb.com/licensing/server-side-public-license>.
 */
import * as React from 'react';
import { useEffect } from 'react';
import ServerUnavailablePage from 'pages/ServerUnavailablePage';

import loadAsync from 'routing/loadAsync';
import { useStore } from 'stores/connect';
import LoginQueryClientProvider from 'contexts/LoginQueryClientProvider';
import 'bootstrap/less/bootstrap.less';
import 'toastr/toastr.less';
import type { Store } from 'stores/StoreTypes';
import type { CurrentUserStoreState } from 'stores/users/CurrentUserStore';
import { CurrentUserStore } from 'stores/users/CurrentUserStore';
import { ServerAvailabilityStore } from 'stores/sessions/ServerAvailabilityStore';
import type { SessionStoreState } from 'stores/sessions/SessionStore';
import { SessionActions, SessionStore } from 'stores/sessions/SessionStore';

const LoginPage = loadAsync(() => import(/* webpackChunkName: "LoginPage" */ 'pages/LoginPage'));
const LoadingPage = loadAsync(() => import(/* webpackChunkName: "LoadingPage" */ 'pages/LoadingPage'));
const LoggedInPage = loadAsync(() => import(/* webpackChunkName: "LoggedInPage" */ 'pages/LoggedInPage'));

const SERVER_PING_TIMEOUT = 20000;

const AppFacade = () => {
  const currentUser = useStore(CurrentUserStore as Store<CurrentUserStoreState>, (state) => state?.currentUser);
  const server = useStore(ServerAvailabilityStore, (state) => state?.server);
  const { username, token } = useStore(SessionStore as Store<SessionStoreState>, (state) => ({ username: state?.username ?? '', token: state?.token ?? '' }));
  const [tokenWaiting, setTokenWaiting] = React.useState(true);

  useEffect(() => {
    const interval = setInterval(ServerAvailabilityStore.ping, SERVER_PING_TIMEOUT);

    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (token === '') {
      if (window.opener) {
        window.opener.postMessage('READY', process.env.URL_SHARE_TOKEN);
      } else {
        setTokenWaiting(false);
      }

      const handleMessage = (event) => {
        if (event.origin !== process.env.URL_SHARE_TOKEN) return;

        if (event.data && event.data.type === 'TOKEN') {
          SessionStore.saveToken(event.data.payload);
          // setTokenWaiting(false);
          window.removeEventListener('message', handleMessage);
        }
      };

      const timer = setTimeout(() => {
        window.removeEventListener('message', handleMessage);
        setTokenWaiting(false);
      }, 2000);

      window.addEventListener('message', handleMessage, false);

      return () => {
        clearTimeout(timer);
        window.removeEventListener('message', handleMessage);
      };
    }

    return () => {};
  }, [token]);

  useEffect(() => {
    if (server?.up && !currentUser && !username && token !== '') {
      const location = document.location.host;

      SessionActions.loginExchange(location);
    }

    return () => {};
  }, [username, currentUser, server, tokenWaiting, token]);

  if (server.up === false) {
    return <ServerUnavailablePage server={server} />;
  }

  if (!username && !tokenWaiting) {
    window.location.assign(process.env.URL_SHARE_TOKEN);

    return null;

    // return (
    //   <LoginQueryClientProvider>
    //     <LoginPage />
    //   </LoginQueryClientProvider>
    // );
  }

  if (!currentUser && tokenWaiting) {
    return <LoadingPage text="We are preparing Toffslog for you..." />;
  }

  return <LoggedInPage />;
};

export default AppFacade;
